<template>
  <fts-design-modal width="1280px" class="fts-logic-service-design-modal" ref="ftsDesignModal">
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title">
        <font-awesome-icon class="design-modal-header-icon default" :icon="['fas', 'scroll']" />
        <input
          type="text"
          placeholder="Please enter a name"
          class="fts-form-control design-modal-header-input"
          :class="{error: $v.formData.name && $v.formData.name.$error}"
          v-model="formData.name"
          spellcheck="false"
          ref="designerName"
        />
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">{{error('name')}}</div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Logic</span>
      <span class="design-modal-header-description">extra boost of logics</span>
    </template>
    <!-- body -->
    <template #body>
      <!-- description -->
      <div class="fts-form-group">
        <fts-contenteditable
          v-model="formData.description"
          text-wrap
          placeholder="enter description (optional)"
        />
      </div>
      <!-- script -->
      <div class="design-modal-form-group">
        <fts-script-editor
          editor-id="logicServiceScript"
          :save-button="!!saveCallBack"
          v-model="formData.script"
          lang="python"
          label="Script"
          @save="done(true, true)"
          @fullview-change="scriptFullView = $event"
        >
          <template #label="{ label }">
            {{label}} <fts-release-badge class="fts-align-top" />
          </template>
        </fts-script-editor>
      </div>
      <!-- debug output (internal or beta users) -->
      <div class="design-modal-form-group" v-if="canDebug">
        <fts-debug-panel
          ref="debugPanel"
          state-key="logicServiceScript"
          :inline="!scriptFullView"
          :floating="scriptFullView"
          test-button
          :is-testing="isTesting"
          @test="testLogic"
        />
      </div>
      <!-- payload -->
      <div class="design-modal-form-group">
        <fts-script-editor
          editor-id="logicServicePayload"
          v-model="formData.payload"
          lang="python"
          label="Payload"
          disabled-dictionary
        >
          <template #label="{ label }">
            {{label}} <fts-release-badge class="fts-align-top" />
          </template>
        </fts-script-editor>
      </div>

      <fts-form-element
        :form-data="formData"
        :item="{
          fieldName: 'public',
          input: 'toggleSwitch',
        }"
        comfort-design
        class="fts-col"
      />

      <fts-form-element
        :form-data="formData"
        :item="{
          fieldName: 'actual',
          input: 'toggleSwitch',
        }"
        comfort-design
        class="fts-col"
        v-if="isInternal"
      >
        <template #formLabel="{ label }">
          {{label}} <fts-release-badge alpha class="fts-align-top" />
        </template>
      </fts-form-element>
    </template>
    <!-- footer -->
    <template #footerLeft>
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </template>
    <template #footerRight>
      <fts-btn text @click="beforeClose">Cancel</fts-btn>

      <fts-btn color="primary" text @click="done(true)" v-if="Boolean(saveCallBack)">
        <font-awesome-icon :icon="['fas', 'check']" />Save
      </fts-btn>

      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" />Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import changesets from "diff-json";
import validator from "../commons/designValidator";
import { authorizationStore } from '../store/authorizationStore';

import FTSDesignModal from "./FTSDesignModal";
import FTSBtn from "../elements/FTSBtn";
import FTSFormElement from "../elements/FTSFormElement";
import FTSScriptEditor from "./FTSScriptEditor";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSDebugPanel from "../elements/FTSDebugPanel";

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-btn": FTSBtn,
    "fts-form-element": FTSFormElement,
    "fts-script-editor": FTSScriptEditor,
    "fts-contenteditable": FTSContenteditable,
    "fts-debug-panel": FTSDebugPanel
  },
  data: () => ({
    formData: null,
    originalData: null,
    resolve: null,
    saveCallBack: null,
    isTesting: false,
    scriptFullView: false
  }),
  validations() {
    let formData = {};
    formData = validator.logicServiceDesign(this.logicServices);
    return { formData };
  },
  methods: {
    async show(item, saveCallBack) {
      this.saveCallBack = saveCallBack;
      this.formData = _.cloneDeep(item);

      if (!this.formData.script) this.$set(this.formData, "script", "");
      if (!this.formData.payload) this.$set(this.formData, "payload", "");

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    }
                  }
                ]
              });
            } else {
              this.reset();
            }
          }
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
        });
      });
    },
    done(save, enableToast) {
      this.$v.formData.$touch();

      if (this.$v.formData.$invalid) {
        if (enableToast) {
          this.$toasted.error("Configuration error. Please ensure all values are in a proper format.");
        }
        return;
      }

      if (save) {
        this.saveCallBack && this.saveCallBack(this.formData);
        this.originalData = _.cloneDeep(this.formData);
      } else {
        this.resolve && this.resolve(this.formData);
        this.reset();
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    async remove() {
      const res = await this.$confirmation.show({
        type: 'delete',
        title: 'Logic',
        text: this.formData.name
      });

      if (res === true) {
        this.resolve && this.resolve(true);
        this.reset();
      }
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    error(key) {
      return validator.logicServiceDesignError(this.$v.formData, key);
    },
    async testLogic() {
      try {
        this.isTesting = true;

        if (this.$refs.debugPanel) {
          this.$refs.debugPanel.clear();
        }

        await this.$store.dispatch("testConfiguration", {
          item: this.formData,
          type: "logicService",
        });

        this.$toasted.success("Test completed successfully");
      } catch(err) {
        if (this.$refs.debugPanel) {
          this.$refs.debugPanel.logError(err);
        }
        this.$toasted.global.error({
          response: err.response
        });
      } finally {
        this.isTesting = false;
      }
    }
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    canDebug() {
      return authorizationStore.getters.isInternal || authorizationStore.getters.isBeta;
    },
    logicServices() {
      return this.$store.getters.canvasLogicServices;
    },
    isExist() {
      return this.logicServices.some(x => x.id === this.formData.id);
    }
  }
};
</script>
